import * as R from 'ramda';
import { createSelector } from 'reselect';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
//////////////////////////////////////////////////

const store = R.path(['fleetProfile', 'driver']);

const selectDriverGeneralDetails = createSelector(
  R.path(['fleetProfile', 'driver', 'generalDetails']),
  (generalDetails) => {
    if (R.isNil(generalDetails)) return;

    const { assignedDivision, domicileTerminal, unemploymentReason } = generalDetails;

    return {
      ...generalDetails,
      [GC.FIELD_ASSIGNED_DIVISION]: G.getDropdownOptionGuidFromObject(assignedDivision),
      [GC.FIELD_DOMICILE_TERMINAL]: G.getDropdownOptionGuidFromObject(domicileTerminal),
      [GC.FIELD_UNEMPLOYMENT_REASON]: R.propOr('', GC.FIELD_DROPDOWN_OPTION_GUID, unemploymentReason),
    };
  },
);

const selectUserGuid = createSelector(
  R.path(['fleetProfile', 'driver', 'generalDetails', 'userGuid']),
  (userGuid) => R.join('', R.split('', R.or(userGuid, ''))),
);

const makeSelectGeneralTabEntities = () => createSelector(
  store,
  (state) => {
    const payToLocation = R.path(['generalDetails', GC.SYSTEM_OBJECT_PAY_TO_LOCATION], state);

    return {
      ...R.pick(
        [
          'contacts',
          'references',
          'driverHours',
          'integrationList',
          'safetyViolation',
          'employmentHistory',
          'lastKnownLocation',
          'truckIntegrationList',
          'teamDriverIntegrationList',
        ],
        state,
      ),
      [GC.SYSTEM_OBJECT_PAY_TO_LOCATION]: G.ifElse(G.isNilOrEmpty(payToLocation), [], R.of(Array, payToLocation)),
    };
  },
);

const makeSelectSafetyViolation = () => createSelector(
  store,
  ({ safetyViolation }) => safetyViolation,
);

const makeSelectLicenseDocumentsTabEntities = () => createSelector(
  store,
  (state) => {
    const { documents } = state;

    const documentsFilter = R.path(['formGroupFilters', 'documentsFilter'], state);

    const filteredDocuments = R.compose(
      R.filter((item) => R.or(
        G.isNilOrEmpty(documentsFilter),
        R.pathEq(documentsFilter, [GC.FIELD_DOCUMENT_DOCUMENT_TYPE, GC.FIELD_DROPDOWN_OPTION_GUID], item),
      )),
      R.pathOr([], ['documents']),
    )(state);

    return {
      ...R.pick(['license', 'citizenship', 'certifications'], state),
      documents: G.ifElse(G.isNotNil(documents), filteredDocuments, documents),
    };
  },
);

const makeSelectDriverPaidByTrip = () => createSelector(
  store,
  R.path(['generalDetails', GC.FIELD_DRIVER_PAID_BY_TRIP]),
);

const makeSelectRatingEngineTabEntities = () => createSelector(
  store,
  ({ accessorialList, compensationList, sharedAccessorialList }) => ({
    accessorialList,
    compensationList,
    sharedAccessorialList: R.isNil(sharedAccessorialList) ? sharedAccessorialList : R.filter(
      R.propEq(true, GC.FIELD_SHARED),
      sharedAccessorialList,
    ),
  }),
);

const makeSelectPayrollTabEntities = () => createSelector(
  store,
  (state) => {
    const { payrollCompensation } = state;

    return {
      ...R.pick(
        ['deductionSettings', 'payrollAccessorialList', 'monthlyPayrollDeductionList'],
        state,
      ),
      payrollCompensation: G.ifElse(
        G.isNotNilAndNotEmpty(payrollCompensation),
        R.assocPath([0, 'expanded'], true, payrollCompensation),
        payrollCompensation,
      ),
    };
  },
);

const makeSelectDriverDailyLogTabEntities = () => createSelector(
  store,
  R.pick(['dailyHoursList']),
);

const makeSelectAdvancePaymentTabEntities = () => createSelector(
  store,
  R.pick(['advancePaymentList']),
);

const makeSelectDriverExpenseTabEntities = () => createSelector(
  store,
  R.pick(['driverExpenseList']),
);

const makeSelectDriverInfoForAdvancePayment = () => createSelector(
  store,
  R.compose(
    R.pick([
      'userGuid',
      GC.FIELD_GUID,
      GC.FIELD_LAST_NAME,
      GC.FIELD_FIRST_NAME,
      GC.FIELD_USER_LOGIN_ID,
    ]),
    R.pathOr({}, ['generalDetails']),
  ),
);

const makeSelectUnavailablePeriodsTabEntities = () => createSelector(
  store,
  R.pick(['unavailablePeriodList']),
);

const makeSelectUnavailablePeriodDatesFilter = () => createSelector(
  store,
  R.path(['formGroupFilters', 'unavailablePeriodDatesFilter']),
);

const makeSelectUnavailablePeriodReasonFilter = () => createSelector(
  store,
  R.path(['formGroupFilters', 'unavailablePeriodReasonFilter']),
);

const makeSelectDriverExpenseTypes = () => createSelector(
  store,
  R.path(['driverExpenseTypes']),
);

const makeSelectDriverAssignmentHistoryTabEntities = () => createSelector(
  store,
  R.pick([
    'truckAssignmentHistory',
    'trailerAssignmentHistory',
    'teamDriverAssignmentHistory',
    'fleetVendorAssignmentHistory',
  ]),
);

const makeSelectResetAssignmentHistoryReportByAssignmentType = () => createSelector(
  store,
  R.prop('resetAssignmentHistory'),
);

export {
  // common
  selectUserGuid,
  selectDriverGeneralDetails,
  // driver
  makeSelectSafetyViolation,
  makeSelectDriverPaidByTrip,
  makeSelectGeneralTabEntities,
  makeSelectPayrollTabEntities,
  makeSelectRatingEngineTabEntities,
  makeSelectDriverExpenseTabEntities,
  makeSelectDriverDailyLogTabEntities,
  makeSelectAdvancePaymentTabEntities,
  makeSelectDriverInfoForAdvancePayment,
  makeSelectLicenseDocumentsTabEntities,
  makeSelectUnavailablePeriodsTabEntities,
  makeSelectDriverAssignmentHistoryTabEntities,
  makeSelectResetAssignmentHistoryReportByAssignmentType,
  // filters
  makeSelectUnavailablePeriodDatesFilter,
  makeSelectUnavailablePeriodReasonFilter,
  // expenses
  makeSelectDriverExpenseTypes,
};
